import { useEffect, useMemo, useRef, useState } from "react";
import { ChatAvatar } from "../../components/chat-avatar.js";
import type { ChatViewModel } from "../../lib/store.js";
import { useRealtimeStore } from "../../lib/store.js";

const MAX_RESULTS = 20;

const scoreMatch = (title: string, query: string): number => {
  if (query.length === 0) {
    return 1;
  }
  const lowerTitle = title.toLowerCase();
  const lowerQuery = query.toLowerCase();
  if (lowerTitle === lowerQuery) {
    return 1000;
  }
  if (lowerTitle.startsWith(lowerQuery)) {
    return 500 - lowerTitle.length;
  }
  const wordBoundaryIndex = lowerTitle
    .split(/[\s_\-./]+/)
    .findIndex((word) => word.startsWith(lowerQuery));
  if (wordBoundaryIndex >= 0) {
    return 250 - wordBoundaryIndex * 5 - lowerTitle.length;
  }
  const substringIndex = lowerTitle.indexOf(lowerQuery);
  if (substringIndex >= 0) {
    return 100 - substringIndex - lowerTitle.length;
  }
  return -1;
};

const highlightMatch = (title: string, query: string): Array<string | { match: string }> => {
  if (query.length === 0) {
    return [title];
  }
  const lowerTitle = title.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerTitle.indexOf(lowerQuery);
  if (index < 0) {
    return [title];
  }
  return [
    title.slice(0, index),
    { match: title.slice(index, index + query.length) },
    title.slice(index + query.length)
  ];
};

// Внешний компонент держит только hotkey и флаг open. Тело (подписка на
// state.chats + скоринг 10k тайтлов) монтируется ТОЛЬКО когда свитчер открыт:
// раньше useShallow по рекорду из 10k чатов сравнивал все ключи на каждое
// WS-событие, даже когда свитчер был закрыт.
export const QuickSwitcher = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent): void => {
      // ⌘M отдан Command Palette (действия, см. features/command-palette).
      // Быстрый переход к чату переехал на ⌘J. Сравниваем по event.code,
      // т.к. на русской раскладке event.key возвращает «о» и Ctrl+J не
      // ловился у саппортов на Windows.
      const isToggle = (event.metaKey || event.ctrlKey) && event.code === "KeyJ";
      if (isToggle) {
        event.preventDefault();
        setOpen((value) => !value);
        return;
      }
      if (event.key === "Escape" && open) {
        event.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open]);

  if (!open) {
    return null;
  }

  return <QuickSwitcherPanel onClose={() => setOpen(false)} />;
};

const QuickSwitcherPanel = ({ onClose }: { onClose: () => void }) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const chats = useRealtimeStore((state) => state.chats);
  const selectChat = useRealtimeStore((state) => state.selectChat);
  // Серверный поиск (раунд 47) — тот же слайс, что у строки поиска в
  // сайдбаре: state.chats знает только догруженные чаты, worker ищет по
  // всей базе TDLib (имя, @username, Telegram ID).
  const dialogSearch = useRealtimeStore((state) => state.dialogSearch);
  const searchDialogs = useRealtimeStore((state) => state.searchDialogs);
  const clearDialogSearch = useRealtimeStore((state) => state.clearDialogSearch);
  const trimmedQuery = query.trim();
  useEffect(() => {
    if (trimmedQuery.length < 2) {
      clearDialogSearch();
      return;
    }
    const timer = window.setTimeout(() => searchDialogs(trimmedQuery), 350);
    return () => window.clearTimeout(timer);
  }, [trimmedQuery, searchDialogs, clearDialogSearch]);
  useEffect(() => () => clearDialogSearch(), [clearDialogSearch]);
  const serverResultsFresh = dialogSearch.query === trimmedQuery && trimmedQuery.length >= 2;
  const serverSearchLoading = serverResultsFresh && dialogSearch.loading;

  const results = useMemo(() => {
    const trimmed = query.trim();
    const lowerTrimmed = trimmed.toLowerCase();
    const scored: Array<{ chat: ChatViewModel; score: number }> = [];
    for (const chat of Object.values(chats)) {
      let score = scoreMatch(chat.title, trimmed);
      if (
        score < 0 &&
        trimmed.length > 0 &&
        (chat.chatId === trimmed || String(chat.telegramUserId ?? "") === lowerTrimmed)
      ) {
        score = 900;
      }
      if (score >= 0) {
        scored.push({ chat, score });
      }
    }
    scored.sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }
      return right.chat.lastActivityAt.localeCompare(left.chat.lastActivityAt);
    });
    const local = scored.slice(0, MAX_RESULTS).map((entry) => entry.chat);
    if (!serverResultsFresh || dialogSearch.chatIds.length === 0) {
      return local;
    }
    const seen = new Set(local.map((chat) => chat.chatId));
    const remote: ChatViewModel[] = [];
    for (const chatId of dialogSearch.chatIds) {
      const chat = chats[chatId];
      if (chat && !seen.has(chatId)) {
        seen.add(chatId);
        remote.push(chat);
      }
    }
    return remote.length === 0 ? local : [...local, ...remote];
  }, [chats, query, serverResultsFresh, dialogSearch.chatIds]);

  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }
    const active = list.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    if (active) {
      active.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, results]);

  const handleSelect = (chat: ChatViewModel): void => {
    selectChat(chat.chatId);
    onClose();
  };

  return (
    <div className="quick-switcher" role="dialog" aria-label="Быстрый поиск чатов">
      <button
        type="button"
        className="quick-switcher__backdrop"
        aria-label="Закрыть"
        onClick={onClose}
      />
      <div className="quick-switcher__panel" onClick={(event) => event.stopPropagation()}>
        <div className="quick-switcher__input-wrap">
          <span className="quick-switcher__icon" aria-hidden="true">
            <i className="ic ic-search" aria-hidden="true" />
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Поиск чата…"
            className="quick-switcher__input"
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setActiveIndex((index) => Math.min(results.length - 1, index + 1));
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                setActiveIndex((index) => Math.max(0, index - 1));
              } else if (event.key === "Enter") {
                event.preventDefault();
                const target = results[activeIndex];
                if (target) {
                  handleSelect(target);
                }
              }
            }}
          />
          <span className="quick-switcher__hint">Esc</span>
        </div>
        <div ref={listRef} className="quick-switcher__results">
          {results.length === 0 ? (
            <div className="quick-switcher__empty">
              {serverSearchLoading ? "Ищем в Telegram…" : "Ничего не найдено"}
            </div>
          ) : (
            results.map((chat, index) => {
              const parts = highlightMatch(chat.title, query.trim());
              return (
                <button
                  key={chat.chatId}
                  type="button"
                  data-index={index}
                  className={
                    index === activeIndex
                      ? "quick-switcher__row quick-switcher__row--active"
                      : "quick-switcher__row"
                  }
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => handleSelect(chat)}
                >
                  <ChatAvatar
                    title={chat.isSavedMessages ? "Избранное" : chat.title}
                    avatarUrl={chat.avatarUrl}
                    className="quick-switcher__avatar"
                    savedMessages={chat.isSavedMessages ?? false}
                  >
                    {!chat.isSavedMessages && !chat.isGroupChat && chat.isPeerOnline ? (
                      <span className="status-dot status-dot--online" aria-hidden="true" />
                    ) : null}
                  </ChatAvatar>
                  <div className="quick-switcher__meta">
                    <div className="quick-switcher__title">
                      {parts.map((part, partIndex) =>
                        typeof part === "string" ? (
                          <span key={partIndex}>{part}</span>
                        ) : (
                          <mark key={partIndex} className="quick-switcher__mark">
                            {part.match}
                          </mark>
                        )
                      )}
                    </div>
                    {chat.lastMessageText ? (
                      <div className="quick-switcher__preview">{chat.lastMessageText}</div>
                    ) : null}
                  </div>
                  {chat.unreadCount > 0 ? (
                    <span className="quick-switcher__badge">{chat.unreadCount}</span>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
        <div className="quick-switcher__footer">
          <kbd>↑↓</kbd> навигация · <kbd>Enter</kbd> открыть · <kbd>Esc</kbd> закрыть
        </div>
      </div>
    </div>
  );
};
